import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaUser, FaLock, FaEye, FaEyeSlash, FaSearch, FaExternalLinkAlt, FaTimes } from 'react-icons/fa';

const Login = ({ onLogin }) => {
  const [formData, setFormData] = useState({
    brugernavn: '',
    adgangskode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPublicLookup, setShowPublicLookup] = useState(false);
  const [publicLookup, setPublicLookup] = useState('');
  const [publicError, setPublicError] = useState('');
  const [publicResults, setPublicResults] = useState([]);
  const [publicLoading, setPublicLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotIdentifier, setForgotIdentifier] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const publicLookupController = useRef(null);

  const publicPlaceholder = useMemo(
    () => 'Vogn nr., registreringsnummer eller stelnummer',
    []
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        onLogin(data.user, data.token);
      } else {
        setError(data.message || 'Login fejlede');
      }
    } catch (err) {
      setError('Der opstod en fejl under login');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handlePublicSearch = (e) => {
    e.preventDefault();
    setPublicError('');
    const lookup = publicLookup.trim();
    if (!lookup) {
      setPublicError('Indtast vogn nr., registreringsnummer eller stelnummer');
      return;
    }

    const params = new URLSearchParams({ lookup });
    openPublicProfile(params);
  };

  const handleTogglePublicLookup = () => {
    setShowPublicLookup((prev) => !prev);
    setError('');
    setPublicError('');
    setPublicLookup('');
    setPublicResults([]);
    if (publicLookupController.current) {
      publicLookupController.current.abort();
      publicLookupController.current = null;
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    const identifier = forgotIdentifier.trim();
    if (!identifier) {
      setForgotError('Indtast brugernavn eller e-mail');
      return;
    }
    setForgotLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier })
      });
      const data = await response.json();
      if (!response.ok) {
        setForgotError(data?.message || 'Kunne ikke sende adgangskode');
        return;
      }
      setForgotMessage(data?.message || 'Hvis brugeren findes, er der sendt en mail med en midlertidig adgangskode.');
      setForgotIdentifier('');
    } catch (err) {
      setForgotError('Der opstod en fejl. Prøv igen.');
    } finally {
      setForgotLoading(false);
    }
  };

  const openPublicProfile = (params) => {
    const url = `/api/biler/offentlig-profile?${params.toString()}`;
    window.open(url, '_blank', 'noopener');
  };

  const handleSelectPublicResult = (result) => {
    const params = new URLSearchParams();
    if (result.internt_nummer) {
      params.set('internt_nummer', result.internt_nummer);
    } else if (result.nummerplade) {
      params.set('nummerplade', result.nummerplade);
    } else if (result.stelnummer) {
      params.set('stelnummer', result.stelnummer);
    }
    openPublicProfile(params);
  };

  useEffect(() => {
    if (!showPublicLookup) return undefined;

    const lookup = publicLookup.trim();
    if (publicLookupController.current) {
      publicLookupController.current.abort();
    }

    if (!lookup || lookup.length < 2) {
      setPublicResults([]);
      setPublicLoading(false);
      publicLookupController.current = null;
      return undefined;
    }

    const controller = new AbortController();
    publicLookupController.current = controller;
    setPublicLoading(true);

    const fetchResults = async () => {
      try {
        const params = new URLSearchParams({ lookup });
        const response = await fetch(`/api/biler/offentlig-suggest?${params.toString()}`, {
          signal: controller.signal,
        });
        if (!response.ok) throw new Error('lookup-failed');
        const data = await response.json();
        setPublicResults(Array.isArray(data) ? data : []);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setPublicResults([]);
          setPublicError('Kunne ikke hente resultater');
        }
      } finally {
        if (!controller.signal.aborted) {
          setPublicLoading(false);
          publicLookupController.current = null;
        }
      }
    };

    fetchResults();
    return () => controller.abort();
  }, [publicLookup, showPublicLookup]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-red-600 to-red-800 dark:from-red-900 dark:to-red-800 px-0">
      <div className="max-w-md w-full bg-white/95 dark:bg-gray-900/95 backdrop-blur border border-red-200/40 dark:border-red-800/40 rounded-2xl shadow-2xl p-8 mx-4 my-6">
        <div className="text-center mb-8">
          <div className="mx-auto h-16 w-16 rounded-xl overflow-hidden ring-2 ring-white/70 dark:ring-white/20 shadow mb-4 bg-white">
            <img src="/favicon.svg" alt="Falck CV" className="h-full w-full object-cover" />
          </div>
          <p className="text-gray-600 dark:text-gray-300 mt-2">Log ind på din konto</p>
        </div>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {!showPublicLookup ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Brugernavn
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaUser className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="brugernavn"
                  value={formData.brugernavn}
                  onChange={handleChange}
                  required
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Indtast dit brugernavn"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Adgangskode
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setForgotMessage('');
                    setForgotError('');
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Glemt adgangskode?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaLock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="adgangskode"
                  value={formData.adgangskode}
                  onChange={handleChange}
                  required
                  className="block w-full pl-10 pr-12 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder="Indtast din adgangskode"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500"
                >
                  {showPassword ? (
                    <FaEyeSlash className="h-5 w-5" />
                  ) : (
                    <FaEye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            <div className="space-y-4">
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-4 rounded-lg text-white font-medium text-lg ${
                  loading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900'
                } transition-colors duration-200`}
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Logger ind...
                  </div>
                ) : (
                  'Log ind'
                )}
              </button>

              <button
                type="button"
                onClick={handleTogglePublicLookup}
                className="block w-full text-center text-falck-blue font-medium hover:underline"
              >
                Vis offentlig bil profil
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={handlePublicSearch} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Søg offentlig bilprofil
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={publicLookup}
                  onChange={(e) => {
                    setPublicLookup(e.target.value);
                    setPublicError('');
                  }}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  placeholder={publicPlaceholder}
                />
              </div>
              {publicError && (
                <div className="text-sm text-red-600 dark:text-red-300 mt-2">{publicError}</div>
      )}

              {!publicError && publicLookup.trim().length >= 2 && (
                <div className="mt-3 space-y-2">
                  {publicLoading && (
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2" />
                      Søger...
                    </div>
                  )}

                  {!publicLoading && publicResults.length === 0 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">Ingen resultater endnu</div>
                  )}

                  {publicResults.length > 0 && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm">
                      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                        {publicResults.map((result) => {
                          const key = result.id || `${result.internt_nummer || ''}-${result.nummerplade || ''}-${result.stelnummer || ''}`;
                          const primaryLabel = result.internt_nummer || result.nummerplade || result.stelnummer || 'Ukendt køretøj';
                          const secondaryLabel = [result.nummerplade, result.stelnummer, result.station_navn]
                            .filter(Boolean)
                            .join(' · ');

                          return (
                            <li key={key}>
                              <button
                                type="button"
                                onClick={() => handleSelectPublicResult(result)}
                                className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/70 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-800"
                              >
                                <div className="font-semibold text-gray-900 dark:text-gray-100">{primaryLabel}</div>
                                {secondaryLabel && (
                                  <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{secondaryLabel}</div>
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-falck-blue text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <FaExternalLinkAlt className="h-4 w-4" />
                Åbn offentlig profil
              </button>

              <button
                type="button"
                onClick={handleTogglePublicLookup}
                className="block w-full text-center text-falck-blue font-medium hover:underline"
              >
                Returner til login
              </button>
            </div>
          </form>
        )}
      </div>
      {showForgotPassword && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6 sm:py-10 overflow-y-auto">
          <div
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            aria-hidden="true"
            onClick={() => setShowForgotPassword(false)}
          ></div>
          <div className="relative z-10 w-full max-w-md border border-gray-200 dark:border-gray-700 shadow-2xl rounded-2xl bg-white dark:bg-gray-900 p-5 sm:p-6">
            <button
              type="button"
              onClick={() => setShowForgotPassword(false)}
              className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Luk"
            >
              <FaTimes className="h-4 w-4" />
            </button>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Glemt adgangskode</h3>
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Brugernavn eller e-mail
                </label>
                <input
                  type="text"
                  value={forgotIdentifier}
                  onChange={(e) => setForgotIdentifier(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-falck-blue focus:border-transparent bg-white dark:bg-gray-800 dark:text-gray-100"
                  placeholder="fx brugernavn eller email"
                />
              </div>
              {forgotMessage && (
                <div className="bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-200 px-4 py-2 rounded-lg">
                  {forgotMessage}
                </div>
              )}
              {forgotError && (
                <div className="bg-red-50 dark:bg-red-900/40 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-200 px-4 py-2 rounded-lg">
                  {forgotError}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(false)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Luk
                </button>
                <button
                  type="submit"
                  disabled={forgotLoading}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-falck-blue hover:bg-falck-dark-blue disabled:opacity-60"
                >
                  {forgotLoading ? 'Sender...' : 'Send adgangskode'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
